import { useMemo, useState, useEffect, useRef } from 'react';
import { Trash2, SearchX, ChevronDown } from 'lucide-react';

import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from 'src/lib/utils';

type RepositorySelectorOption = {
  value: string;
  label: string;
};

type RepositorySelectorProps = {
  assetsCount?: number;
  options: RepositorySelectorOption[];
  loading?: boolean;
  refetch: () => void | Promise<unknown>;
  value?: string;
  repoName?: string;
  onValueChange?: (value: string) => void;
  onCreateAsset?: (name: string) => void;
  className?: string;
  disabled?: boolean;
};

const RepositorySelector = ({
  options,
  loading,
  refetch,
  value,
  repoName,
  onValueChange,
  onCreateAsset,
  className,
  disabled,
}: RepositorySelectorProps) => {
  const [search, setSearch] = useState(repoName || '');
  const [open, setOpen] = useState(false);
  const [createdName, setCreatedName] = useState<string | null>(repoName || null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (repoName) {
      setCreatedName(repoName);
      setSearch(repoName);
    } else if (repoName === '' && createdName) {
      setCreatedName(null);
    }
  }, [repoName]);

  useEffect(() => {
    if (createdName) return;
    if (!value) return;

    const current = options.find((o) => o.value === value);
    if (current && !search) {
      setSearch(current.label || current.value);
    }
  }, [value, createdName, options, search]);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const query = search.toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(query));
  }, [options, search]);

  const trimmedSearch = search.trim();

  const nameAlreadyExists = useMemo(() => {
    if (!trimmedSearch) return false;
    const target = trimmedSearch.toLowerCase();
    return options.some((option) => option.label.toLowerCase() === target);
  }, [options, trimmedSearch]);

  const canCreate = !!trimmedSearch && !nameAlreadyExists;
  const hasSelection = !!value || !!createdName;

  return (
    <div className={className}>
      <Label htmlFor="repo-input">Asset name</Label>
      <Popover modal={true} open={open} onOpenChange={setOpen}>
        {hasSelection ? (
          <div className="flex items-center gap-2 mt-2">
            <div className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm items-center flex-1">
              {search}
            </div>
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="shrink-0"
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                setSearch('');
                setCreatedName(null);
                onValueChange?.('');
                setOpen(true);
                inputRef.current?.focus();
                await refetch();
              }}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ) : (
          <PopoverTrigger asChild>
            <div className="flex items-center gap-2 mt-2">
              <div className="relative flex-1">
                <Input
                  ref={inputRef}
                  id="repo-input"
                  placeholder={loading ? 'Loading assets...' : 'Search or enter new asset name'}
                  value={search}
                  maxLength={100}
                  disabled={disabled || loading}
                  onChange={(e) => {
                    const next = e.target.value.slice(0, 100);
                    setSearch(next);
                    if (value || createdName) {
                      onValueChange?.('');
                      setCreatedName(null);
                    }
                  }}
                  className="pr-8"
                />
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
              </div>
            </div>
          </PopoverTrigger>
        )}
        <PopoverContent
          className="w-(--radix-popover-trigger-width) p-0 max-h-64 overflow-y-auto"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="pt-1">
            <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground">Your repositories</div>
            {filteredOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={cn(
                  'w-full px-3 py-1.5 text-left text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground',
                  value === option.value && 'bg-accent text-accent-foreground',
                )}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCreatedName(null);
                  onValueChange?.(option.value);
                  setSearch(option.label || option.value);
                  setOpen(false);
                  inputRef.current?.blur();
                }}
              >
                {option.label || option.value}
              </button>
            ))}

            {canCreate && (
              <Button
                type="button"
                variant="ghost"
                className="w-full justify-start px-3 py-1.5 text-sm text-foreground"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const name = search.trim();
                  if (!name) return;
                  setCreatedName(name);
                  onCreateAsset?.(name);
                  setOpen(false);
                  inputRef.current?.blur();
                }}
              >
                Create &quot;{search.trim()}&quot;
              </Button>
            )}

            {!filteredOptions.length && !canCreate && (
              <div className="p-4 min-h-[180px] flex items-center justify-center">
                <div className="w-full h-full min-h-[160px] border border-dashed border-muted-foreground/30 rounded-md flex flex-col items-center justify-center gap-3">
                  <div className="flex items-center justify-center p-3 border rounded-lg shadow-sm">
                    <SearchX className="size-6 text-muted-foreground" />
                  </div>
                  <div className="text-sm text-muted-foreground">No assets found</div>
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default RepositorySelector;
export type { RepositorySelectorProps, RepositorySelectorOption };
